import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import './common/DataTable.css';
import './NewAssistant.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import materiaService from '../services/MateriaService';
import cursoService from '../services/CursoService';
import areaService from '../services/AreaService';
import divisionService from '../services/DivisionService';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';
import usuarioService from '../services/UsuarioService';
import SearchableSelect from './common/SearchableSelect';

const getProfesorId = (profesor) => String(profesor?.id || profesor?.Id || '');

const getProfesorLabel = (profesor) => (
  profesor?.displayName
  || `${profesor?.nombre || ''} ${profesor?.apellido || ''}`.trim()
  || profesor?.email
  || 'Profesor'
);

const getCursoId = (curso) => String(curso?.id || curso?.Id || curso?.idCurso || curso?.IdCurso || '');

const getCursoValorAnio = (curso) => (
  curso?.valorAnio
  ?? curso?.ValorAnio
  ?? curso?.anio
  ?? curso?.Anio
  ?? null
);

const getCursoDivision = (curso) => (
  curso?.division
  || curso?.Division
  || ''
);

const getCursoCicloLectivo = (curso) => (
  curso?.cicloLectivo
  ?? curso?.CicloLectivo
  ?? null
);

const normalizeDivision = (value) => String(value || '').trim().toUpperCase();

const getCursoLabel = (curso) => {
  const valorAnio = getCursoValorAnio(curso);
  const division = getCursoDivision(curso);
  const cicloLectivo = getCursoCicloLectivo(curso);
  const base = valorAnio ? `${valorAnio}° ${division}`.trim() : (curso?.nombre || curso?.Nombre || 'Curso');
  return cicloLectivo ? `${base} · ${cicloLectivo}` : base;
};

const normalizeCursoAsignado = (curso, persisted = true) => ({
  ...curso,
  persisted
});

const AdminEditMateria = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { materiaId } = useParams();
  const { user, logout } = useAuth();
  const materiaState = useMemo(() => location.state?.materia || null, [location.state]);
  const stateColegioId = location.state?.idColegio || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const [areas, setAreas] = useState([]);
  const [anios, setAnios] = useState([]);
  const [divisiones, setDivisiones] = useState([]);
  const [profesoresDisponibles, setProfesoresDisponibles] = useState([]);
  const [profesoresLoading, setProfesoresLoading] = useState(false);
  const [profesoresError, setProfesoresError] = useState('');
  const [selectedProfesorId, setSelectedProfesorId] = useState('');
  const [profesoresAsignados, setProfesoresAsignados] = useState([]);
  const [isAssociating, setIsAssociating] = useState(false);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [cursosLoading, setCursosLoading] = useState(false);
  const [cursosError, setCursosError] = useState('');
  const [selectedCursoId, setSelectedCursoId] = useState('');
  const [cursosAsignados, setCursosAsignados] = useState([]);
  const [isAssociatingCurso, setIsAssociatingCurso] = useState(false);
  const [removingCursoId, setRemovingCursoId] = useState('');
  const [unidades, setUnidades] = useState([]);
  const [unidadesLoading, setUnidadesLoading] = useState(false);
  const [unidadesError, setUnidadesError] = useState('');
  const [unidadAction, setUnidadAction] = useState({ open: false, type: '', unidad: null, nextActivo: null });
  const [unidadActionLoading, setUnidadActionLoading] = useState(false);
  const [unidadActionLoadingId, setUnidadActionLoadingId] = useState(null);
  const [unidadForm, setUnidadForm] = useState({ nombre: '', descripcion: '' });
  const [editingUnidadId, setEditingUnidadId] = useState(null);
  const [isUnidadSaving, setIsUnidadSaving] = useState(false);

  const [form, setForm] = useState({
    nombre: materiaState?.nombre || materiaState?.Nombre || '',
    codigo: materiaState?.codigo || materiaState?.Codigo || '',
    descripcion: materiaState?.descripcion || materiaState?.Descripcion || '',
    idArea: materiaState?.idArea || materiaState?.IdArea || '',
    idAnio: materiaState?.idAnio || materiaState?.IdAnio || '',
    division: materiaState?.division || materiaState?.Division || '',
    idColegio: activeColegioId || ''
  });
  const [persistedMateria, setPersistedMateria] = useState({
    idAnio: materiaState?.idAnio || materiaState?.IdAnio || '',
    division: materiaState?.division || materiaState?.Division || '',
    idColegio: materiaState?.idColegio || materiaState?.IdColegio || activeColegioId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const profesorOptions = useMemo(() => {
    const assignedIds = new Set(
      profesoresAsignados.map((profesor) => getProfesorId(profesor)).filter(Boolean)
    );

    return profesoresDisponibles
      .filter((profesor) => {
        const profesorId = getProfesorId(profesor);
        return profesorId && !assignedIds.has(profesorId);
      })
      .map((profesor) => {
        const label = getProfesorLabel(profesor);
        const email = profesor.email || profesor.Email || '';

        return {
          value: getProfesorId(profesor),
          label,
          secondary: email,
          searchText: `${label} ${email}`
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [profesoresAsignados, profesoresDisponibles]);

  const cursoOptions = useMemo(() => {
    const assignedIds = new Set(
      cursosAsignados.map((curso) => getCursoId(curso)).filter(Boolean)
    );
    const currentDivision = normalizeDivision(form.division);

    return cursosDisponibles
      .filter((curso) => {
        const cursoId = getCursoId(curso);
        const cursoAnioId = String(curso?.idAnio || curso?.IdAnio || '');
        const cursoDivision = normalizeDivision(getCursoDivision(curso));
        return cursoId
          && !assignedIds.has(cursoId)
          && (!form.idAnio || cursoAnioId === String(form.idAnio))
          && (!currentDivision || cursoDivision === currentDivision);
      })
      .map((curso) => {
        const label = getCursoLabel(curso);
        const valorAnio = getCursoValorAnio(curso);
        const division = getCursoDivision(curso) || 'Sin división';
        const cicloLectivo = getCursoCicloLectivo(curso) || 'Sin ciclo lectivo';
        const secondary = `Ciclo lectivo ${cicloLectivo} · Año ${valorAnio || 'Sin año'} · División ${division}`;

        return {
          value: getCursoId(curso),
          label,
          secondary,
          searchText: `${label} ${secondary} ${curso?.nombre || curso?.Nombre || ''}`
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [cursosAsignados, cursosDisponibles, form.division, form.idAnio]);

  const sortedCursosAsignados = useMemo(() => (
    cursosAsignados
      .slice()
      .sort((a, b) => getCursoLabel(a).localeCompare(getCursoLabel(b), 'es', { sensitivity: 'base' }))
  ), [cursosAsignados]);

  const isAnioChanged = String(form.idAnio || '') !== String(persistedMateria.idAnio || '');
  const isDivisionChanged = normalizeDivision(form.division) !== normalizeDivision(persistedMateria.division);
  const isMateriaScopeChanged = isAnioChanged || isDivisionChanged;

  const divisionOptions = useMemo(() => {
    const options = Array.isArray(divisiones) ? divisiones.slice() : [];
    const currentDivision = String(form.division || '').trim();
    const exists = options.some((division) => (
      String(division.nombre || division.Nombre || '').trim().toUpperCase() === currentDivision.toUpperCase()
    ));

    if (currentDivision && !exists) {
      options.unshift({
        id: `current-${currentDivision}`,
        nombre: currentDivision,
        descripcion: 'División actual'
      });
    }

    return options;
  }, [divisiones, form.division]);

  useEffect(() => {
    if (!materiaId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [materiaId, navigate]);

  useEffect(() => {
    const fetchMateria = async () => {
      if (materiaState) return;
      if (!materiaId) return;
      try {
        const m = await materiaService.getMateriaById(materiaId);
        if (m) {
          setForm({
            nombre: m.nombre || m.Nombre || '',
            codigo: m.codigo || m.Codigo || '',
            descripcion: m.descripcion || m.Descripcion || '',
            idArea: m.idArea || m.IdArea || '',
            idAnio: m.idAnio || m.IdAnio || '',
            division: m.division || m.Division || '',
            idColegio: m.idColegio || m.IdColegio || activeColegioId || ''
          });
          setPersistedMateria({
            idAnio: m.idAnio || m.IdAnio || '',
            division: m.division || m.Division || '',
            idColegio: m.idColegio || m.IdColegio || activeColegioId || ''
          });
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar la materia.');
        setMessageType('error');
      }
    };
    fetchMateria();
  }, [materiaId, materiaState, activeColegioId]);

  useEffect(() => {
    const fetchProfesores = async () => {
      if (!activeColegioId) return;
      setProfesoresLoading(true);
      setProfesoresError('');
      try {
        const list = await usuarioService.getProfesoresPorColegio(activeColegioId);
        setProfesoresDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setProfesoresError(error.message || 'No pudimos cargar los profesores.');
      } finally {
        setProfesoresLoading(false);
      }
    };

    fetchProfesores();
  }, [activeColegioId]);

  useEffect(() => {
    const fetchProfesoresAsignados = async () => {
      if (!materiaId) return;
      try {
        const list = await materiaService.getProfesoresPorMateria(materiaId);
        setProfesoresAsignados(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los profesores asociados.');
        setMessageType('error');
      }
    };

    fetchProfesoresAsignados();
  }, [materiaId]);

  useEffect(() => {
    const fetchCursosDisponibles = async () => {
      if (!activeColegioId) return;
      setCursosLoading(true);
      setCursosError('');
      try {
        const list = await cursoService.getCursosActivos({ idColegio: activeColegioId });
        setCursosDisponibles(Array.isArray(list) ? list : []);
      } catch (error) {
        setCursosDisponibles([]);
        setCursosError(error.message || 'No pudimos cargar los cursos activos.');
      } finally {
        setCursosLoading(false);
      }
    };

    fetchCursosDisponibles();
  }, [activeColegioId]);

  useEffect(() => {
    const fetchCursosAsignados = async () => {
      if (!materiaId) return;
      try {
        const list = await materiaService.getCursosPorMateria(materiaId);
        setCursosAsignados(Array.isArray(list) ? list.map((curso) => normalizeCursoAsignado(curso, true)) : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los cursos asociados.');
        setMessageType('error');
      }
    };

    fetchCursosAsignados();
  }, [materiaId]);

  useEffect(() => {
    const fetchUnidades = async () => {
      if (!materiaId) return;
      setUnidadesLoading(true);
      setUnidadesError('');
      try {
        const list = await materiaService.getUnidades(materiaId);
        setUnidades(Array.isArray(list) ? list : []);
      } catch (error) {
        setUnidadesError(error.message || 'No pudimos cargar las unidades.');
      } finally {
        setUnidadesLoading(false);
      }
    };

    fetchUnidades();
  }, [materiaId]);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const list = await areaService.getAreasActivas(activeColegioId);
        setAreas(Array.isArray(list) ? list : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar las áreas.');
        setMessageType('error');
      }
    };
    fetchAreas();

    const fetchAnios = async () => {
      try {
        const list = await materiaService.getAniosActivos();
        setAnios(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar años:', error);
      }
    };
    const fetchDivisiones = async () => {
      if (!activeColegioId) return;
      try {
        const list = await divisionService.getDivisionesActivas(activeColegioId);
        setDivisiones(Array.isArray(list) ? list : []);
      } catch (error) {
        console.warn('No pudimos cargar divisiones:', error);
      }
    };
    fetchAnios();
    fetchDivisiones();

    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
    if (activeColegioId && !persistedMateria.idColegio) {
      setPersistedMateria((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio, persistedMateria.idColegio]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'idAnio' && !value ? { division: '' } : {})
    }));
  };

  const handleAddProfesor = () => {
    if (!selectedProfesorId) return;
    const profesor = profesoresDisponibles.find((p) => getProfesorId(p) === String(selectedProfesorId));
    if (!profesor) return;
    if (profesoresAsignados.some((p) => getProfesorId(p) === String(selectedProfesorId))) return;
    setProfesoresAsignados((prev) => [...prev, profesor]);
    setSelectedProfesorId('');
  };

  const handleAddCurso = () => {
    if (!selectedCursoId) return;
    const curso = cursosDisponibles.find((item) => getCursoId(item) === String(selectedCursoId));
    if (!curso) return;
    if (cursosAsignados.some((item) => getCursoId(item) === String(selectedCursoId))) return;
    setCursosAsignados((prev) => [...prev, normalizeCursoAsignado(curso, false)]);
    setSelectedCursoId('');
  };

  const handleRemoveProfesor = async (profesorId) => {
    if (!materiaId || !profesorId) return;
    try {
      await materiaService.removeProfesores(materiaId, [profesorId]);
      setProfesoresAsignados((prev) => prev.filter((p) => (p.id || p.Id) !== profesorId));
      setMessage('Profesor desasociado correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos desasociar el profesor.');
      setMessageType('error');
    }
  };

  const reloadCursosAsignados = async () => {
    if (!materiaId) return;
    const list = await materiaService.getCursosPorMateria(materiaId);
    setCursosAsignados(Array.isArray(list) ? list.map((curso) => normalizeCursoAsignado(curso, true)) : []);
  };

  const handleRemoveCurso = async (curso) => {
    const cursoId = getCursoId(curso);
    if (!cursoId) return;

    if (!curso?.persisted) {
      setCursosAsignados((prev) => prev.filter((item) => getCursoId(item) !== cursoId));
      return;
    }

    setRemovingCursoId(cursoId);
    try {
      await materiaService.removeCursos(materiaId, [cursoId]);
      await reloadCursosAsignados();
      setMessage('Curso desasociado correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos desasociar el curso.');
      setMessageType('error');
    } finally {
      setRemovingCursoId('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre de la materia.');
      setMessageType('error');
      return;
    }
    if (!form.idArea) {
      setMessage('Selecciona un área para asociar la materia.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await materiaService.updateMateria(materiaId, {
        nombre: form.nombre,
        codigo: form.codigo,
        descripcion: form.descripcion,
        idColegio: form.idColegio,
        idArea: form.idArea,
        idAnio: form.idAnio || null,
        division: form.idAnio ? (form.division || '').trim() || null : null
      });
      setPersistedMateria({
        idAnio: form.idAnio || '',
        division: form.division || '',
        idColegio: form.idColegio
      });
      setMessage('Materia actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'materias' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar la materia.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignProfesores = async () => {
    if (!materiaId) return;
    const ids = profesoresAsignados.map((p) => p.id || p.Id).filter(Boolean);
    if (ids.length === 0) {
      setMessage('Agrega al menos un profesor para asociar.');
      setMessageType('error');
      return;
    }

    setIsAssociating(true);
    setMessage('Asociando profesores a la materia...');
    setMessageType('info');
    try {
      const res = await materiaService.assignProfesores(materiaId, ids);
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const reactivados = res?.reactivados || res?.Reactivados || [];
      const yaAsociados = res?.yaAsociados || res?.YaAsociados || [];
      const noEncontrados = res?.profesoresNoEncontrados || res?.ProfesoresNoEncontrados || [];
      const noRol = res?.profesoresNoRol || res?.ProfesoresNoRol || [];

      const summaryParts = [];
      if (nuevos.length) summaryParts.push(`Asociados nuevos: ${nuevos.length}`);
      if (reactivados.length) summaryParts.push(`Reactivados: ${reactivados.length}`);
      if (yaAsociados.length) summaryParts.push(`Ya asociados: ${yaAsociados.length}`);
      if (noEncontrados.length) summaryParts.push(`No encontrados: ${noEncontrados.length}`);
      if (noRol.length) summaryParts.push(`Sin rol Profesor: ${noRol.length}`);

      setMessage(`Asociación completada. ${summaryParts.join(' | ') || ''}`.trim());
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos asociar los profesores.');
      setMessageType('error');
    } finally {
      setIsAssociating(false);
    }
  };

  const handleAssignCursos = async () => {
    if (!materiaId) return;
    const ids = cursosAsignados.map((curso) => getCursoId(curso)).filter(Boolean);
    if (ids.length === 0) {
      setMessage('Agrega al menos un curso para asociar.');
      setMessageType('error');
      return;
    }
    if (!form.idAnio) {
      setMessage('La materia debe tener un año asignado para poder asociarse a cursos.');
      setMessageType('error');
      return;
    }
    if (isMateriaScopeChanged) {
      setMessage('Guardá primero el año y la división de la materia antes de asociarla a cursos.');
      setMessageType('error');
      return;
    }

    setIsAssociatingCurso(true);
    setMessage('Asociando cursos a la materia...');
    setMessageType('info');
    try {
      const res = await materiaService.assignCursos(materiaId, ids);
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const reactivados = res?.reactivados || res?.Reactivados || [];
      const yaAsociados = res?.yaAsociados || res?.YaAsociados || [];
      const noEncontrados = res?.cursosNoEncontrados || res?.CursosNoEncontrados || [];
      const noColegio = res?.cursosNoColegio || res?.CursosNoColegio || [];
      const noMismoAnio = res?.cursosNoMismoAnio || res?.CursosNoMismoAnio || [];
      const noMismaDivision = res?.cursosNoMismaDivision || res?.CursosNoMismaDivision || [];
      const inactivos = res?.cursosInactivos || res?.CursosInactivos || [];

      await reloadCursosAsignados();

      const summaryParts = [];
      if (nuevos.length) summaryParts.push(`Asociados nuevos: ${nuevos.length}`);
      if (reactivados.length) summaryParts.push(`Reactivados: ${reactivados.length}`);
      if (yaAsociados.length) summaryParts.push(`Ya asociados: ${yaAsociados.length}`);
      if (noEncontrados.length) summaryParts.push(`No encontrados: ${noEncontrados.length}`);
      if (noColegio.length) summaryParts.push(`De otro colegio: ${noColegio.length}`);
      if (noMismoAnio.length) summaryParts.push(`De otro año: ${noMismoAnio.length}`);
      if (noMismaDivision.length) summaryParts.push(`De otra división: ${noMismaDivision.length}`);
      if (inactivos.length) summaryParts.push(`Inactivos: ${inactivos.length}`);

      setMessage(`Asociación de cursos completada. ${summaryParts.join(' | ') || ''}`.trim());
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos asociar los cursos.');
      setMessageType('error');
    } finally {
      setIsAssociatingCurso(false);
    }
  };

  const resetUnidadForm = () => {
    setUnidadForm({ nombre: '', descripcion: '' });
    setEditingUnidadId(null);
  };

  const handleUnidadSubmit = async () => {
    if (!materiaId) return;
    const nombre = (unidadForm.nombre || '').trim();
    if (!nombre) {
      setMessage('Completa el nombre de la unidad.');
      setMessageType('error');
      return;
    }

    setIsUnidadSaving(true);
    setMessage(editingUnidadId ? 'Actualizando unidad...' : 'Creando unidad...');
    setMessageType('info');
    try {
      if (editingUnidadId) {
        await materiaService.updateUnidad(editingUnidadId, {
          nombre,
          descripcion: unidadForm.descripcion
        });
      } else {
        await materiaService.createUnidad({
          nombre,
          descripcion: unidadForm.descripcion,
          idMateria: materiaId
        });
      }
      const list = await materiaService.getUnidades(materiaId);
      setUnidades(Array.isArray(list) ? list : []);
      resetUnidadForm();
      setMessage('Unidad guardada correctamente.');
      setMessageType('success');
    } catch (error) {
      setMessage(error.message || 'No pudimos guardar la unidad.');
      setMessageType('error');
    } finally {
      setIsUnidadSaving(false);
    }
  };

  const handleEditUnidad = (unidad) => {
    setEditingUnidadId(unidad.id || unidad.Id);
    setUnidadForm({
      nombre: unidad.nombre || unidad.Nombre || '',
      descripcion: unidad.descripcion || unidad.Descripcion || ''
    });
  };

  const handleToggleUnidad = (unidad) => {
    const id = unidad.id || unidad.Id;
    if (!id) return;
    const nextActivo = !(unidad.activo ?? unidad.Activo);
    setUnidadAction({ open: true, type: 'toggle', unidad, nextActivo });
  };

  const handleDeleteUnidad = (unidadId) => {
    if (!unidadId) return;
    const unidad = unidades.find((u) => (u.id || u.Id) === unidadId);
    if (!unidad) return;
    setUnidadAction({ open: true, type: 'delete', unidad, nextActivo: null });
  };

  const closeUnidadAction = () => {
    if (unidadActionLoading) return;
    setUnidadAction({ open: false, type: '', unidad: null, nextActivo: null });
  };

  const confirmUnidadAction = async () => {
    if (!unidadAction.unidad) return;
    const id = unidadAction.unidad.id || unidadAction.unidad.Id;
    if (!id) return;
    const nombre = unidadAction.unidad.nombre || unidadAction.unidad.Nombre || 'Unidad';
    setUnidadActionLoading(true);
    setUnidadActionLoadingId(id);
    try {
      if (unidadAction.type === 'toggle') {
        const nextActivo = !!unidadAction.nextActivo;
        await materiaService.updateUnidadActivo(id, nextActivo);
        setUnidades((prev) =>
          prev.map((u) => {
            const uid = u.id || u.Id;
            if (uid !== id) return u;
            return { ...u, activo: nextActivo, Activo: nextActivo };
          })
        );
        setMessage(nextActivo ? `Unidad "${nombre}" activada correctamente.` : `Unidad "${nombre}" desactivada correctamente.`);
        setMessageType('success');
      } else if (unidadAction.type === 'delete') {
        await materiaService.deleteUnidad(id);
        setUnidades((prev) => prev.filter((u) => (u.id || u.Id) !== id));
        setMessage(`Unidad "${nombre}" eliminada correctamente.`);
        setMessageType('success');
      }
      setUnidadAction({ open: false, type: '', unidad: null, nextActivo: null });
    } catch (error) {
      setMessage(error.message || 'No pudimos completar la acción de la unidad.');
      setMessageType('error');
    } finally {
      setUnidadActionLoading(false);
      setUnidadActionLoadingId(null);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="materias"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar materia</h1>
            <p className="page-subtitle">Ajusta los datos de la materia en el colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-book" />
                <span>Datos de la materia</span>
              </div>
            </div>
            <div className="card-content course-structure-layout">
              <div>
                <p style={{ marginTop: 0, color: '#475569' }}>
                  Completa o ajusta los datos de la materia.
                </p>
                <form onSubmit={handleSubmit} className="prof-create-form">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Materia X"
                    />
                  </div>
                  <div className="form-field">
                    <label>Código</label>
                    <input
                      type="text"
                      value={form.codigo || ''}
                      onChange={(e) => handleChange('codigo', e.target.value)}
                      placeholder="Ej: AED-101"
                    />
                  </div>
                  <div className="form-field">
                    <label>Descripción</label>
                    <textarea
                      value={form.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      placeholder="Descripción de la materia"
                      style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Área (obligatorio)</label>
                    <select
                      value={form.idArea}
                      onChange={(e) => handleChange('idArea', e.target.value)}
                    >
                      <option value="">Selecciona un área</option>
                      {areas.map((area) => (
                        <option key={area.id || area.Id} value={area.id || area.Id}>
                          {area.nombre || area.name || area.descripcion || area.detalle || 'Área'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Año</label>
                    <select
                      value={form.idAnio || ''}
                      onChange={(e) => handleChange('idAnio', e.target.value)}
                    >
                      <option value="">Sin año</option>
                      {anios.map((anio) => (
                        <option key={anio.id || anio.Id} value={anio.id || anio.Id}>
                          {anio.valor || anio.Valor}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>División</label>
                    <select
                      value={form.division || ''}
                      onChange={(e) => handleChange('division', e.target.value)}
                      disabled={!form.idAnio}
                    >
                      <option value="">{form.idAnio ? 'Sin división específica' : 'Selecciona primero un año'}</option>
                      {divisionOptions.map((division) => (
                        <option key={division.id || division.Id || division.nombre || division.Nombre} value={division.nombre || division.Nombre || ''}>
                          {division.nombre || division.Nombre}
                        </option>
                      ))}
                    </select>
                    <small className="helper-text">
                      {form.idAnio
                        ? 'Opcional. Si defines división, la materia solo podrá asociarse a cursos de esa división.'
                        : 'La división solo aplica cuando la materia tiene año asignado.'}
                    </small>
                  </div>

                  {message && (
                    <div
                      className="modal-message"
                      style={{
                        marginTop: '0.5rem',
                        borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                        background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                        color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                      }}
                    >
                      {(isSubmitting || isAssociating || isAssociatingCurso || !!removingCursoId) && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                      {message}
                    </div>
                  )}

                  <div className="form-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'materias' } })}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                      <span>Guardar cambios</span>
                    </button>
                  </div>
                </form>
              </div>

              <div className="course-structure-panel">
                <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                  <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                  <span>Profesores</span>
                </div>
                <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
                  Selecciona los profesores para esta materia. Los ya asociados se muestran abajo.
                </p>

                {profesoresLoading && <p>Cargando profesores...</p>}
                {profesoresError && <p style={{ color: '#b91c1c' }}>{profesoresError}</p>}

                {!profesoresLoading && profesoresDisponibles.length > 0 && (
                  <div className="form-field">
                    <label>Seleccionar profesor</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <SearchableSelect
                          value={selectedProfesorId}
                          onChange={setSelectedProfesorId}
                          options={profesorOptions}
                          placeholder="Busca un profesor por nombre o email"
                          emptyText="No hay más profesores disponibles para asociar."
                          noResultsText="No encontramos profesores con esa búsqueda."
                          disabled={profesoresLoading || profesorOptions.length === 0}
                        />
                      </div>
                      <button type="button" className="btn btn-primary" onClick={handleAddProfesor} disabled={!selectedProfesorId}>
                        <i className="fas fa-plus" />
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Profesores asignados</p>
                  {profesoresAsignados.length === 0 && (
                    <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay profesores asignados.</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {profesoresAsignados.map((prof) => {
                      const name = getProfesorLabel(prof);
                      const profId = getProfesorId(prof);
                      return (
                        <div key={profId} style={{ border: '1px solid var(--gray-200)', borderRadius: '999px', padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem', background: '#f8fafc' }}>
                          <i className="fas fa-chalkboard-teacher" style={{ color: '#3b82f6' }} />
                          <span>{name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveProfesor(profId)}
                            style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer' }}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                  <button type="button" className="btn btn-primary" onClick={handleAssignProfesores} disabled={isAssociating}>
                    {isAssociating ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-link" />}
                    <span>{isAssociating ? 'Asociando...' : 'Asociar profesor'}</span>
                  </button>
                </div>

                <div style={{ marginTop: '2rem' }}>
                  <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                    <i className="fas fa-school" style={{ color: '#3b82f6' }} />
                    <span>Cursos asociados</span>
                  </div>
                  <p className="course-structure-note">
                    Asocia esta materia a los cursos del mismo año. El selector muestra ciclo lectivo, año y división para que ubiques rápido cada comisión.
                  </p>

                  {isMateriaScopeChanged && (
                    <div className="course-structure-warning">
                      Cambiaste el año o la división de la materia. Guardá primero ese cambio antes de asociarla a cursos para evitar inconsistencias.
                    </div>
                  )}

                  <div className="form-field">
                    <label>Agregar curso a la materia</label>
                    <div className="course-structure-toolbar">
                      <div style={{ flex: 1 }}>
                        <SearchableSelect
                          value={selectedCursoId}
                          onChange={setSelectedCursoId}
                          options={cursoOptions}
                          placeholder="Busca por ciclo lectivo, año o división"
                          emptyText={
                            !form.idAnio
                              ? 'Asigná primero un año a la materia para poder elegir cursos.'
                              : 'No hay más cursos activos disponibles para este alcance.'
                          }
                          noResultsText="No encontramos cursos con esa búsqueda."
                          disabled={cursosLoading || !form.idAnio || isMateriaScopeChanged || cursoOptions.length === 0}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleAddCurso}
                        disabled={!selectedCursoId || !form.idAnio || isMateriaScopeChanged}
                      >
                        <i className="fas fa-plus" />
                      </button>
                    </div>
                    {cursosLoading && <small className="helper-text">Cargando cursos activos...</small>}
                    {!cursosLoading && cursosError && <small className="helper-text" style={{ color: '#b91c1c' }}>{cursosError}</small>}
                    {!cursosLoading && !cursosError && (
                      <small className="helper-text">
                        {form.division
                          ? 'Solo se muestran cursos del mismo año y de la misma división que la materia.'
                          : 'Solo se muestran cursos del mismo año que la materia.'}
                      </small>
                    )}
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Cursos asignados</p>
                    {sortedCursosAsignados.length === 0 && (
                      <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay cursos asociados.</p>
                    )}
                    <div className="course-chip-stack">
                      {sortedCursosAsignados.map((curso) => {
                        const cursoId = getCursoId(curso);
                        const valorAnio = getCursoValorAnio(curso);
                        const division = getCursoDivision(curso) || 'Sin división';
                        const cicloLectivo = getCursoCicloLectivo(curso) || 'Sin ciclo lectivo';
                        const isRemoving = removingCursoId === cursoId;

                        return (
                          <div key={cursoId} className="course-profesor-chip">
                            <div>
                              <span>{getCursoLabel(curso)}</span>
                              <small>{`Ciclo lectivo ${cicloLectivo} · Año ${valorAnio || 'Sin año'} · División ${division}`}</small>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveCurso(curso)}
                              title="Quitar curso de la materia"
                              disabled={isRemoving}
                            >
                              {isRemoving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-times" />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                    <button type="button" className="btn btn-primary" onClick={handleAssignCursos} disabled={isAssociatingCurso}>
                      {isAssociatingCurso ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-link" />}
                      <span>{isAssociatingCurso ? 'Asociando...' : 'Asociar cursos'}</span>
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: '2rem' }}>
                  <div className="card-title" style={{ marginBottom: '0.75rem' }}>
                    <i className="fas fa-layer-group" style={{ color: '#3b82f6' }} />
                    <span>Unidades</span>
                  </div>
                  <p style={{ marginTop: 0, color: '#475569', fontSize: '0.95rem' }}>
                    Administra las unidades de esta materia.
                  </p>

                  {unidadesLoading && (
                    <p>
                      <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />
                      Cargando unidades...
                    </p>
                  )}
                  {unidadesError && <p style={{ color: '#b91c1c' }}>{unidadesError}</p>}

                  {!unidadesLoading && (
                    <div className="form-field" style={{ marginBottom: '1rem' }}>
                      <label>Nombre de unidad</label>
                      <input
                        type="text"
                        value={unidadForm.nombre}
                        onChange={(e) => setUnidadForm((prev) => ({ ...prev, nombre: e.target.value }))}
                        placeholder="Unidad 1"
                      />
                      <label style={{ marginTop: '0.75rem' }}>Descripción</label>
                      <textarea
                        value={unidadForm.descripcion}
                        onChange={(e) => setUnidadForm((prev) => ({ ...prev, descripcion: e.target.value }))}
                        placeholder="Descripción breve"
                        style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '70px' }}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <button type="button" className="btn btn-primary" onClick={handleUnidadSubmit} disabled={isUnidadSaving}>
                          {isUnidadSaving ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                          <span>{editingUnidadId ? 'Guardar cambios' : 'Agregar unidad'}</span>
                        </button>
                        {editingUnidadId && (
                          <button type="button" className="btn btn-secondary" onClick={resetUnidadForm} disabled={isUnidadSaving}>
                            Cancelar edición
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <p style={{ margin: '0 0 0.5rem 0', color: '#475569', fontWeight: 600 }}>Unidades cargadas</p>
                    {unidades.length === 0 && (
                      <p style={{ color: '#6b7280', margin: 0 }}>Aún no hay unidades registradas.</p>
                    )}
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {unidades.map((unidad) => {
                        const uid = unidad.id || unidad.Id;
                        const activo = unidad.activo ?? unidad.Activo;
                        return (
                          <div key={uid} style={{ border: '1px solid var(--gray-200)', borderRadius: '12px', padding: '0.75rem 0.9rem', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
                              <div>
                                <p style={{ margin: 0, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>{unidad.nombre || unidad.Nombre}</span>
                                  <span
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      padding: '0.2rem 0.5rem',
                                      borderRadius: '999px',
                                      background: activo ? 'rgba(16,185,129,0.12)' : 'rgba(248,113,113,0.12)',
                                      color: activo ? '#047857' : '#b91c1c'
                                    }}
                                  >
                                    {activo ? 'Activa' : 'Inactiva'}
                                  </span>
                                </p>
                                {unidad.descripcion || unidad.Descripcion ? (
                                  <p style={{ margin: '0.25rem 0 0 0', color: '#64748b' }}>{unidad.descripcion || unidad.Descripcion}</p>
                                ) : null}
                              </div>
                              <div className="table-actions">
                                <button
                                  type="button"
                                  className="table-btn table-btn-edit"
                                  onClick={() => handleEditUnidad(unidad)}
                                  title="Editar unidad"
                                  disabled={unidadActionLoading && unidadActionLoadingId === uid}
                                >
                                  {unidadActionLoading && unidadActionLoadingId === uid && unidadAction.type === 'edit'
                                    ? <i className="fas fa-spinner fa-spin" />
                                    : <i className="fas fa-edit" />}
                                </button>
                                <button
                                  type="button"
                                  className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
                                  onClick={() => handleToggleUnidad(unidad)}
                                  title={activo ? 'Desactivar unidad' : 'Activar unidad'}
                                  disabled={unidadActionLoading && unidadActionLoadingId === uid}
                                >
                                  {unidadActionLoading && unidadActionLoadingId === uid && unidadAction.type === 'toggle'
                                    ? <i className="fas fa-spinner fa-spin" />
                                    : <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`} />}
                                </button>
                                <button
                                  type="button"
                                  className="table-btn table-btn-delete"
                                  onClick={() => handleDeleteUnidad(uid)}
                                  title="Eliminar unidad"
                                  disabled={unidadActionLoading && unidadActionLoadingId === uid}
                                >
                                  {unidadActionLoading && unidadActionLoadingId === uid && unidadAction.type === 'delete'
                                    ? <i className="fas fa-spinner fa-spin" />
                                    : <i className="fas fa-trash" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {unidadAction.open && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal">
            <h3>
              {unidadAction.type === 'delete' ? 'Eliminar unidad' : (unidadAction.nextActivo ? 'Activar unidad' : 'Desactivar unidad')}
            </h3>
            <p>
              {unidadAction.type === 'delete'
                ? 'Esta acción eliminará la unidad de la materia. No se podrá deshacer.'
                : (unidadAction.nextActivo
                  ? 'Al activar la unidad, estará disponible para selección en asistentes y gestión académica.'
                  : 'Al desactivar la unidad, dejará de estar disponible para selección en asistentes, pero se mantendrá en el historial.')}
            </p>
            <div className="confirm-modal-actions">
              <button className="btn btn-secondary" type="button" onClick={closeUnidadAction} disabled={unidadActionLoading}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="button" onClick={confirmUnidadAction} disabled={unidadActionLoading}>
                {unidadActionLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                <span>Confirmar</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditMateria;
